import pdfMake from 'pdfmake/build/pdfmake';
import type { FontDefinition, LoadedFonts } from '@/modules/viewer/composables/pdfFonts';

export interface InlineStyle {
  fontSize: number;
  font?: string;
  bold?: boolean;
  italics?: boolean;
  characterSpacing?: number;
  [key: string]: unknown;
}

export type Measure = (text: string, style: InlineStyle) => number;

type Direction = 'rtl' | 'ltr' | 'neutral';

interface Segment {
  text: string;
  style: InlineStyle;
}

interface Token {
  segments: Segment[];
  space?: Segment;
  direction: Direction;
  breakAfter: boolean;
}

type PdfNode = Record<string, unknown>;

const INLINE_KEYS = [
  'font',
  'fontSize',
  'bold',
  'italics',
  'color',
  'background',
  'decoration',
  'decorationStyle',
  'decorationColor',
  'link',
  'characterSpacing',
];

const MIRRORED: Record<string, string> = {
  '(': ')',
  ')': '(',
  '[': ']',
  ']': '[',
  '{': '}',
  '}': '{',
  '<': '>',
  '>': '<',
  '«': '»',
  '»': '«',
};

// pdfmake indents list items by the width of a "9. " marker, on top of the list's own margin.
const LIST_MARKER_EM = 1.5;
const TABLE_CELL_PADDING = 10;
// Lines are set with noWrap, so leave slack instead of letting a line run past the margin.
const WIDTH_SLACK = 0.97;

const isArabicLetter = (char: string): boolean => {
  return /\p{L}/u.test(char) && /\p{Script=Arabic}/u.test(char);
};

const isStrongLtr = (text: string): boolean => {
  return /[\p{L}\p{Nd}]/u.test(text);
};

const isRtlParagraph = (text: string): boolean => {
  const firstLetter = text.match(/\p{L}/u)?.[0];
  return firstLetter !== undefined && isArabicLetter(firstLetter);
};

const pickStyle = (node: PdfNode): Partial<InlineStyle> => {
  return Object.fromEntries(INLINE_KEYS.filter(key => key in node).map(key => [key, node[key]]));
};

const marginX = (margin: unknown): number => {
  if (typeof margin === 'number') {
    return margin * 2;
  }
  if (!Array.isArray(margin)) {
    return 0;
  }
  return margin.length === 4 ? margin[0] + margin[2] : (margin[0] ?? 0) * 2;
};

// Returns null when the text holds something other than text inlines, such as an image.
const flatten = (value: unknown, style: InlineStyle): Segment[] | null => {
  if (typeof value === 'string') {
    return [{ text: value, style }];
  }
  if (Array.isArray(value)) {
    const parts = value.map(child => flatten(child, style));
    return parts.every(part => part !== null) ? (parts as Segment[][]).flat() : null;
  }
  if (value && typeof value === 'object' && 'text' in value) {
    return flatten((value as PdfNode).text, { ...style, ...pickStyle(value as PdfNode) });
  }
  return null;
};

const tokenize = (segments: Segment[]): Token[] => {
  const tokens: Token[] = [];
  let current: Token | undefined;

  segments.forEach(({ text, style }) => {
    for (const char of text) {
      if (char === '\n') {
        const last = tokens[tokens.length - 1];
        if (last) {
          last.breakAfter = true;
        }
        current = undefined;
        continue;
      }
      if (/\s/u.test(char)) {
        if (current) {
          current.space = { text: (current.space?.text ?? '') + char, style: current.space?.style ?? style };
        }
        continue;
      }
      if (!current || current.space) {
        current = { segments: [], direction: 'neutral', breakAfter: false };
        tokens.push(current);
      }
      const lastSegment = current.segments[current.segments.length - 1];
      if (lastSegment && lastSegment.style === style) {
        lastSegment.text += char;
      } else {
        current.segments.push({ text: char, style });
      }
    }
  });

  return tokens;
};

const directionOf = (token: Token): Direction => {
  const text = token.segments.map(segment => segment.text).join('');
  if ([...text].some(isArabicLetter)) {
    return 'rtl';
  }
  return isStrongLtr(text) ? 'ltr' : 'neutral';
};

// Punctuation takes the direction of the text around it when both sides agree, and the paragraph's otherwise.
const resolveDirections = (tokens: Token[]): Token[] => {
  const directions = tokens.map(directionOf);
  const strongBefore = (index: number) =>
    directions
      .slice(0, index)
      .reverse()
      .find(direction => direction !== 'neutral');
  const strongAfter = (index: number) => directions.slice(index + 1).find(direction => direction !== 'neutral');

  tokens.forEach((token, index) => {
    if (directions[index] !== 'neutral') {
      token.direction = directions[index];
      return;
    }
    token.direction = strongBefore(index) === 'ltr' && strongAfter(index) === 'ltr' ? 'ltr' : 'rtl';
  });
  return tokens;
};

const widthOf = (segments: Segment[], measure: Measure): number => {
  return segments.reduce((sum, segment) => sum + measure(segment.text, segment.style), 0);
};

const breakLines = (tokens: Token[], maxWidth: number, measure: Measure): Token[][] => {
  const lines: Token[][] = [];
  let line: Token[] = [];
  let width = 0;

  tokens.forEach(token => {
    const tokenWidth = widthOf(token.segments, measure);
    const previousSpace = line[line.length - 1]?.space;
    const gap = previousSpace ? measure(previousSpace.text, previousSpace.style) : 0;

    if (line.length > 0 && width + gap + tokenWidth > maxWidth) {
      lines.push(line);
      line = [token];
      width = tokenWidth;
    } else {
      line.push(token);
      width += gap + tokenWidth;
    }

    if (token.breakAfter) {
      lines.push(line);
      line = [];
      width = 0;
    }
  });

  if (line.length > 0) {
    lines.push(line);
  }
  return lines;
};

const toInline = (segment: Segment, text = segment.text): PdfNode => {
  return { ...segment.style, text };
};

// fontkit lays out an Arabic-script run right to left, digits included, so digit runs are pre-reversed to read left to right.
const shaped = (segment: Segment): PdfNode => {
  if (segment.style.font !== 'NotoSansArabic') {
    return toInline(segment);
  }
  return toInline(
    segment,
    segment.text.replace(/[\p{Nd}٫٬]+/gu, digits => [...digits].reverse().join(''))
  );
};

// Inside right-to-left text, Roboto punctuation must be reversed and its brackets mirrored by hand.
const shapedRtl = (segment: Segment): PdfNode => {
  if (segment.style.font === 'NotoSansArabic' || isStrongLtr(segment.text)) {
    return shaped(segment);
  }
  return toInline(
    segment,
    [...segment.text]
      .reverse()
      .map(char => MIRRORED[char] ?? char)
      .join('')
  );
};

const visualLine = (line: Token[]): PdfNode[] => {
  const units: Token[][] = [];
  line.forEach(token => {
    const last = units[units.length - 1];
    if (token.direction === 'ltr' && last?.[0].direction === 'ltr') {
      last.push(token);
    } else {
      units.push([token]);
    }
  });

  const inlines: PdfNode[] = [];
  units.reverse().forEach((unit, index) => {
    const space = unit[unit.length - 1].space;
    if (index > 0 && space) {
      inlines.push(toInline(space));
    }
    if (unit[0].direction !== 'ltr') {
      inlines.push(...[...unit[0].segments].reverse().map(shapedRtl));
      return;
    }
    unit.forEach((token, tokenIndex) => {
      inlines.push(...token.segments.map(shaped));
      if (tokenIndex < unit.length - 1 && token.space) {
        inlines.push(toInline(token.space));
      }
    });
  });
  return inlines;
};

const layoutParagraph = (node: PdfNode, width: number, style: InlineStyle, measure: Measure): PdfNode => {
  const segments = flatten(node.text, style);
  if (!segments || !isRtlParagraph(segments.map(segment => segment.text).join(''))) {
    return node;
  }

  const lines = breakLines(resolveDirections(tokenize(segments)), width * WIDTH_SLACK, measure);
  const block = Object.fromEntries(Object.entries(node).filter(([key]) => key !== 'text'));
  return {
    ...block,
    stack: lines.map(line => ({ text: visualLine(line), alignment: 'right', noWrap: true })),
  };
};

const walk = (node: unknown, width: number, style: InlineStyle, measure: Measure): unknown => {
  if (Array.isArray(node)) {
    return node.map(child => walk(child, width, style, measure));
  }
  if (!node || typeof node !== 'object') {
    return node;
  }

  const record = node as PdfNode;
  const nodeStyle = { ...style, ...pickStyle(record) };
  const inner = width - marginX(record.margin) - Number(record.marginLeft ?? 0) - Number(record.marginRight ?? 0);

  if ('text' in record) {
    return layoutParagraph(record, inner, nodeStyle, measure);
  }
  if (record.stack) {
    return { ...record, stack: walk(record.stack, inner, nodeStyle, measure) };
  }
  if (record.ul || record.ol) {
    const key = record.ul ? 'ul' : 'ol';
    return { ...record, [key]: walk(record[key], inner - nodeStyle.fontSize * LIST_MARKER_EM, nodeStyle, measure) };
  }
  if (Array.isArray(record.columns)) {
    return { ...record, columns: walk(record.columns, inner / record.columns.length, nodeStyle, measure) };
  }
  const table = record.table as PdfNode | undefined;
  if (table && Array.isArray(table.body)) {
    const body = table.body as unknown[][];
    const cellWidth = inner / Math.max(1, ...body.map(row => row.length)) - TABLE_CELL_PADDING;
    return { ...record, table: { ...table, body: body.map(row => walk(row, cellWidth, nodeStyle, measure)) } };
  }
  return node;
};

// pdfmake 0.2 has no bidi support: it places words left to right, so Arabic lines read backwards. Right-to-left
// paragraphs are broken into lines here and each line is written out in visual order.
export const layoutRtl = (content: unknown, measure: Measure, width: number, fontSize: number): unknown => {
  return walk(content, width, { fontSize }, measure);
};

const fileFor = (font: FontDefinition, style: InlineStyle): string => {
  if (style.bold && style.italics) {
    return font.bolditalics;
  }
  if (style.bold) {
    return font.bold;
  }
  return style.italics ? font.italics : font.normal;
};

export const createMeasure = ({ fonts, vfs }: Required<LoadedFonts>): Measure => {
  // pdfmake has no public text measurement. Without a callback, _createDoc returns the PDFKit document pdfmake
  // lays text out with, so these widths match the export.
  const doc = pdfMake.createPdf({ content: [] }, undefined, fonts, vfs)._createDoc();
  return (text, style) => {
    const file = fileFor(fonts[style.font ?? 'Roboto'], style);
    return doc
      .font(file)
      .fontSize(style.fontSize)
      .widthOfString(text, { characterSpacing: style.characterSpacing ?? 0 });
  };
};
